/**
 * CallRow Component
 *
 * Displays a call record using the UniversalRow slot system.
 * This component simply maps Call data to the appropriate slots.
 *
 * The UniversalRow doesn't know it's rendering a call - it just
 * fills the slots with whatever data is passed.
 */

import React, { useMemo, useState } from 'react';
import { PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import {
  UniversalRow,
  FastActions,
  MetaSeparator,
  PrimaryAction,
  EntityAvatar,
  type FastActionType,
} from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';

// =============================================================================
// TYPES
// =============================================================================

export interface Call {
  id: string; // cal-xxx
  contactId?: string; // cnt-xxx
  contactName?: string;
  phone?: string;
  direction: 'in' | 'out';
  duration?: number; // seconds
  at: string; // ISO date
  notes?: string;
  answered?: boolean;
}

export interface CallRowProps {
  entity: Call;
  onTitleClick?: (call: Call) => void;
  onActionClick?: (actionType: string, callId: string) => void;
  onAvatarClick?: (name: string, id?: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function CallRow({ entity: call, onTitleClick, onActionClick, onAvatarClick }: CallRowProps) {
  const { t } = useTranslation();

  // Primary action state (seen/acknowledged)
  const [isSeen, setIsSeen] = useState(false);

  // Get linked action counts
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(call.id);
  }, [call.id]);

  // Get available actions
  const availableActions = useMemo(() => {
    return getAvailableActions(call.id) as FastActionType[];
  }, [call.id]);

  // === SLOT: leftSlot (Primary Action + Avatar) ===
  const leftSlot = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <PrimaryAction
        type="eye"
        active={isSeen}
        onChange={(seen) => {
          setIsSeen(seen);
          onActionClick?.('seen', call.id);
        }}
      />
      <EntityAvatar
        items={call.contactName ? { id: call.contactId, name: call.contactName } : []}
        type="person"
        size={24}
        onClick={onAvatarClick ? (item) => onAvatarClick(item.name, item.id) : undefined}
      />
    </div>
  );

  // === SLOT: title ===
  const title = call.contactName || call.phone || t('calls.call');

  // === SLOT: subtitle (phone if we have contact name) ===
  const subtitle = call.contactName && call.phone ? call.phone : undefined;

  // === SLOT: icon (direction indicator) ===
  const icon = call.direction === 'in'
    ? <PhoneIncoming size={16} color="#10b981" />
    : <PhoneOutgoing size={16} color="#3b82f6" />;

  // === SLOT: meta (duration info) ===
  const meta = useMemo(() => {
    if (call.answered === false) {
      return <span style={{ color: '#dc2626', fontStyle: 'italic' }}>{t('calls.notAnswered')}</span>;
    }
    if (call.duration !== undefined) {
      const mins = Math.floor(call.duration / 60);
      const secs = call.duration % 60;
      const durationText = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      return <span style={{ color: '#16a34a', fontWeight: 500 }}>{durationText}</span>;
    }
    return null;
  }, [call.duration, call.answered, t]);

  // === SLOT: status (missed call = danger) ===
  const status = call.answered === false ? 'danger' as const : undefined;

  // === SLOT: rightSlot (fast actions) ===
  const rightSlot = availableActions.length > 0 ? (
    <FastActions
      actions={availableActions}
      counts={linkedCounts as Partial<Record<FastActionType, number>>}
      onAction={(action) => onActionClick?.(action, call.id)}
    />
  ) : null;

  return (
    <UniversalRow
      id={call.id}
      leftSlot={leftSlot}
      title={title}
      subtitle={subtitle}
      icon={icon}
      meta={meta}
      date={call.at}
      status={status}
      done={isSeen}
      rightSlot={rightSlot}
      onTitleClick={() => onTitleClick?.(call)}
    />
  );
}
